import React from 'react';
import { SPONSORS, ALUMNI_DONORS } from '../data/images';

// ── SPONSORS SECTION ──────────────────────────────────────────
// Edit sponsor logos and tiers in src/data/images.js → SPONSORS array
// Tiers: 'gold' | 'silver' | 'bronze'
//
// Alumni & donor names (below the sponsor tiers) are edited in
// src/data/images.js → ALUMNI_DONORS array. See the comment there for
// the entry format (name, classYear, tier, anonymous).

const TIER_CONFIG = {
  gold:   { label: 'Gold Sponsors',   size: 140, columns: 3 },
  silver: { label: 'Silver Sponsors', size: 110, columns: 4 },
  bronze: { label: 'Bronze Sponsors', size: 90,  columns: 5 },
};

const DONOR_TIER_STYLE = {
  gold:   { fontSize: 20, fontWeight: 800, color: '#861F41', fontFamily: 'Playfair Display, serif' },
  silver: { fontSize: 17, fontWeight: 700, color: '#1A1A1A' },
  bronze: { fontSize: 15, fontWeight: 600, color: '#555' },
};
const DONOR_DEFAULT_STYLE = { fontSize: 15, fontWeight: 500, color: '#666' };

export default function Sponsors() {
  const gold   = SPONSORS.filter(s => s.tier === 'gold');
  const silver = SPONSORS.filter(s => s.tier === 'silver');
  const bronze = SPONSORS.filter(s => s.tier === 'bronze');

  const tiers = [
    { key: 'gold',   sponsors: gold },
    { key: 'silver', sponsors: silver },
    { key: 'bronze', sponsors: bronze },
  ].filter(t => t.sponsors.length > 0);

  return (
    <section id="sponsors" style={{ padding: '80px 24px', background: 'white' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        <div className="section-header">
          <span className="section-label">Our Partners</span>
          <h2 className="section-title">Thank You to Our Sponsors</h2>
          <p className="section-subtitle">
            Our chapter's success is made possible by generous corporate partners who share our vision of a more diverse and inclusive STEM workforce.
          </p>
        </div>

        {tiers.map(({ key, sponsors }) => {
          const config = TIER_CONFIG[key];
          const medalEmoji = key === 'gold' ? '🥇' : key === 'silver' ? '🥈' : '🥉';
          return (
            <div key={key} style={{ marginBottom: 48 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
              }}>
                <span style={{ fontSize: 20 }}>{medalEmoji}</span>
                <h3 style={{
                  fontSize: 13, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase', color: '#888',
                }}>
                  {config.label}
                </h3>
                <div style={{ flex: 1, height: 1, background: '#EEE' }} />
              </div>

              <div style={{
                display: 'flex', flexWrap: 'wrap',
                gap: key === 'gold' ? 24 : 16,
                justifyContent: 'center',
              }}>
                {sponsors.map((sponsor, i) => (
                  <div key={i} style={{
                    background: 'white',
                    border: '1px solid #EEE',
                    borderRadius: 14, padding: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: config.size + 60,
                    height: key === 'gold' ? 100 : 80,
                    transition: 'all 0.25s ease',
                    cursor: 'default',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = '#D0C0C5'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = '#EEE'; }}
                    title={sponsor.name}>
                    {/* Sponsor logo — UPDATE logos in src/data/images.js SPONSORS array */}
                    <img
                      src={sponsor.logo}
                      alt={sponsor.name}
                      style={{
                        maxWidth: config.size, maxHeight: key === 'gold' ? 60 : 50,
                        objectFit: 'contain', filter: 'grayscale(30%)',
                        transition: 'filter 0.25s ease',
                      }}
                      onMouseEnter={e => e.target.style.filter = 'grayscale(0%)'}
                      onMouseLeave={e => e.target.style.filter = 'grayscale(30%)'}
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <span style={{ display: 'none', fontSize: 13, fontWeight: 700, color: '#555' }}>
                      {sponsor.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* ── ALUMNI & DONOR RECOGNITION ─────────────────────
            Add names in src/data/images.js → ALUMNI_DONORS array.
            Only renders once that array has entries. */}
        {ALUMNI_DONORS && ALUMNI_DONORS.length > 0 && (
          <div style={{
            marginTop: 16, marginBottom: 48,
            padding: '40px 48px', borderRadius: 20,
            background: '#F9F5F6', textAlign: 'center',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 20,
            }}>
              <span style={{ fontSize: 20 }}></span>
              <h3 style={{
                fontSize: 13, fontWeight: 700, letterSpacing: 2,
                textTransform: 'uppercase', color: '#888',
              }}>
                With Gratitude — Our Alumni & Donors
              </h3>
            </div>

            <div style={{
              display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
              alignItems: 'baseline', gap: '10px 26px', marginBottom: 20,
            }}>
              {ALUMNI_DONORS.map((donor, i) => {
                const style = DONOR_TIER_STYLE[donor.tier] || DONOR_DEFAULT_STYLE;
                return (
                  <span key={i} style={style}>
                    {donor.anonymous ? 'Anonymous Donor' : donor.name}
                    {donor.classYear && !donor.anonymous && (
                      <sup style={{ fontSize: '0.55em', marginLeft: 2, color: '#E5751F', fontWeight: 700 }}>
                        {donor.classYear}
                      </sup>
                    )}
                  </span>
                );
              })}
            </div>

            <p style={{ color: '#AAA', fontSize: 12 }}>
              Want to be added to this list?{' '}
              <a href="#donate" style={{ color: '#861F41', fontWeight: 600, textDecoration: 'underline' }}>
                Make a gift
              </a>{' '}
              or email <a href="mailto:shpevt@gmail.com" style={{ color: '#861F41', fontWeight: 600 }}>shpevt@gmail.com</a>.
            </p>
          </div>
        )}

        {/* Become a Sponsor CTA */}
        <div style={{
          marginTop: 16, padding: '40px 48px',
          background: 'linear-gradient(135deg, #F5E8EB 0%, #FFF3E8 100%)',
          borderRadius: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 24,
        }}>
          <div>
            <h3 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 22, fontWeight: 700, color: '#1A1A1A', marginBottom: 8,
            }}>
              Interested in Sponsoring SHPE@VT?
            </h3>
            <p style={{ color: '#666', fontSize: 15, maxWidth: 480 }}>
              Connect with top Hispanic engineering talent at Virginia Tech. Our sponsors gain direct access to motivated students for recruiting, networking, and partnership.
            </p>
          </div>
          <a href="mailto:shpevt@gmail.com?subject=Sponsorship Inquiry" className="btn btn-primary" style={{ flexShrink: 0 }}>
            Become a Sponsor →
          </a>
        </div>
      </div>
    </section>
  );
}